/**
 * Cross-window coordination contract for the one-window-per-document viewer
 * (OR-22558). Each document opens in its own dedicated window: the opener
 * hands it its `appId`/`docId` via the URL and its display metadata
 * (`fileName`/`reviewedBy`) via a `sessionStorage` clone-on-open — no
 * BroadcastChannel round trip is needed for a window to know which document it
 * shows. What *does* still need continuous cross-window signaling is the
 * opener's session state, split into independent signals carried on the
 * same `SessionStateMessage`:
 *   - `sessionState` — auth validity only (`active`/`expired`). Every open
 *     document window gates its *content* on this.
 *   - `heldByMe` — whether the opener currently holds the deal's VLA lock.
 *     Gates only the move/check-in toolbar; losing (or never holding) the
 *     lock does NOT blank the document — it stays viewable read-only.
 *     Deal-scoped via `appId`: an analyst can have multiple deals open as
 *     separate Review tabs at once, each broadcasting independently, and a
 *     document window only adopts `heldByMe` from its own deal's broadcasts.
 *   - `canManageDocuments` — effective matrix permission for the toolbar.
 *     Required alongside lock ownership; missing permission fails closed.
 *
 * Semantics are **load-and-persist**: a window keeps showing its document until
 * the session stops being active. There is deliberately no heartbeat or
 * staleness-clear (a backgrounded/closed opener must not blank a document the
 * analyst is reading).
 *
 * Only identifiers cross the wire — never the presigned URL. The viewer
 * resolves its own short-lived download URL from `appId`/`docId`.
 */

/** BroadcastChannel name. Same-origin only (both windows share the app origin). */
export const CHANNEL_NAME = "dsc-doc-viewer";

/**
 * Session signal the opener sends so the viewer can clear its *content* when
 * the analyst's session is no longer usable. `loggedOut` and `expired` are
 * both derived from an unauthenticated session today (the app can't
 * distinguish them). `lockLost` is reserved but no longer produced by the
 * opener — VLA-lock loss is conveyed via `SessionStateMessage.heldByMe`
 * instead, which gates only the toolbar, not content visibility.
 */
export type SessionState = "active" | "loggedOut" | "expired" | "lockLost";

/** Stamped onto every message by `sendCoord`. */
export interface MessageMeta {
  /** Per-page-load id of the sending window; used to ignore our own messages. */
  tabId: string;
  /** Epoch ms when sent; drives last-writer-wins ordering. */
  sentAt: number;
  /** Unique id per message. */
  msgId: string;
}

/**
 * Opener → viewer(s): the opener's current session signal for a specific deal
 * (`appId`). `sessionState` (auth validity) is effectively global — every open
 * document window gates its *content* on it regardless of which deal it
 * belongs to. `heldByMe` is deal-scoped: it reflects only whether the opener
 * currently holds the VLA In-Use lock for *this* `appId`
 * (`phase === "held-by-me"`), and drives the document viewer's move/check-in
 * toolbar. Documents stay viewable (read-only) even once the lock is
 * released — only the toolbar hides.
 *
 * A viewer window only applies `heldByMe` from messages whose `appId` matches
 * its own document's deal — an analyst can have several deals open as
 * separate Review tabs at once (OR-23xxx), each broadcasting independently,
 * and a window must never adopt another deal's lock state.
 */
export interface SessionStateMessage {
  type: "session-state";
  appId: string;
  sessionState: SessionState;
  heldByMe: boolean;
  /** Effective role/status/lock permission; absent older broadcasts cannot grant toolbar access. */
  canManageDocuments?: boolean;
  meta: MessageMeta;
}

/**
 * Viewer → opener: sent on mount so a freshly-opened window learns the current
 * session state immediately (e.g. a lock already lost before this window
 * existed) instead of waiting for the next change.
 */
export interface RequestSessionStateMessage {
  type: "request-session-state";
  meta: MessageMeta;
}

/** Opener → viewer: user session ended (logout or expiry). Clears viewer state. */
export interface SessionEndedMessage {
  type: "session-ended";
  reason: "logout" | "session-expired";
  meta: MessageMeta;
}

/**
 * Viewer → opener: a `docs/{docId}/actions` call succeeded (check-in, move to a
 * different approval number, and/or move to a different state). Refreshes the
 * opener's docs & messages list.
 *
 * `request` is the exact body sent to the BFF, forwarded as-is so the listener
 * can tell which fields changed (the BFF's `DocMetadataActionResponse` carries
 * only `{ success, message }` — no updated document fields to key off of).
 * `reviewedBy` is only set when `request.checkIn` is true: it's the acting
 * analyst's oraid, resolved client-side (from the authenticated user's
 * `oraid@DOMAIN`-shaped `userName`) since the BFF response doesn't return it,
 * used for an optimistic patch until the follow-up invalidate resyncs with the
 * server's value.
 */
export interface DocumentActionAppliedMessage {
  type: "doc-action-applied";
  appId: string;
  docId: string;
  request: { checkIn?: boolean; moveToApprovalNumber?: number; moveToState?: string };
  reviewedBy?: string;
  meta: MessageMeta;
}

export type CoordMessage =
  SessionStateMessage | RequestSessionStateMessage | SessionEndedMessage | DocumentActionAppliedMessage;

/**
 * Narrows an arbitrary BroadcastChannel payload to a coordination message.
 * @param {unknown} value - The raw `MessageEvent.data`.
 * @returns {boolean} True when the value is one of our coordination messages.
 */
export function isCoordMessage(value: unknown): value is CoordMessage {
  return typeof value === "object" && value !== null && "type" in value && "meta" in value;
}
